import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Clock, TrendingDown, TrendingUp, Zap, Users, Calculator, IndianRupee, BarChart3, Info } from "lucide-react";

// Plan thresholds based on monthly voice screenings needed
// Voice screening = 10% of applicants after resume shortlist (conservative estimate)
const VOICE_SCREEN_RATE = 0.10;

interface RecommendedPlan {
    name: string;
    monthlyPrice: number;
    voiceLimit: number; // per month
    color: string;
}

function getRecommendedPlan(monthlyVoiceNeeded: number): RecommendedPlan {
    if (monthlyVoiceNeeded <= 5) {
        return { name: "Starter", monthlyPrice: 0, voiceLimit: 5, color: "text-slate-600" };
    } else if (monthlyVoiceNeeded <= 25) {
        return { name: "Growth", monthlyPrice: 4999, voiceLimit: 25, color: "text-blue-600" };
    } else if (monthlyVoiceNeeded <= 100) {
        return { name: "Scale", monthlyPrice: 14999, voiceLimit: 100, color: "text-accent" };
    } else {
        return { name: "Enterprise", monthlyPrice: 25000, voiceLimit: Infinity, color: "text-purple-600" };
    }
}

const ROICalculatorSection = () => {
    const [positions, setPositions] = useState(20);
    const [applicantsPerRole, setApplicantsPerRole] = useState(150);
    const [timeToHire, setTimeToHire] = useState(35);
    const [costPerHire, setCostPerHire] = useState(95000);
    const [noShowRate, setNoShowRate] = useState(30);

    const results = useMemo(() => {
        const totalApplicants = positions * applicantsPerRole;

        // Screening hours: AI handles resume parsing instantly
        // Industry avg: 15 min (0.25h) per resume manually
        // AI reduces this by 85% (conservative — it's near-instant but humans still review top shortlist)
        const manualScreeningHoursPerApp = 0.25;
        const totalManualHours = totalApplicants * manualScreeningHoursPerApp;
        const aiScreeningHours = totalManualHours * 0.15;
        const hoursSaved = totalManualHours - aiScreeningHours;

        // Time to hire: 45% faster with AI (industry median: 40–55%)
        const newTimeToHire = Math.round(timeToHire * 0.55);
        const timeSavedDays = (timeToHire - newTimeToHire) * positions;

        // Cost per hire: 35% reduction (conservative; industry range 20–50%)
        const newCostPerHire = Math.round(costPerHire * 0.65);
        const totalCostSavings = (costPerHire - newCostPerHire) * positions;

        // No-show rate: 40% reduction with better pre-screening match
        const newNoShow = Math.round(noShowRate * 0.60);

        const recruiterHoursFreed = Math.round(hoursSaved);
        const recruitersEquivalent = Math.round(hoursSaved / 160 * 10) / 10;

        // Voice screenings needed per month (10% of total applicants, spread quarterly → monthly)
        const monthlyApplicants = totalApplicants / 3;
        const monthlyVoiceNeeded = Math.ceil(monthlyApplicants * VOICE_SCREEN_RATE);
        const plan = getRecommendedPlan(monthlyVoiceNeeded);

        // Use actual plan cost for ROI (annual, billed monthly)
        const intellrAnnualCost = plan.monthlyPrice * 12;

        // Annualise (4 quarters)
        const annualCostSavings = totalCostSavings * 4;

        // ROI: only if Intellr has a cost (Starter is free → infinite ROI, cap display)
        const roiMultiple = intellrAnnualCost > 0
            ? Math.round(annualCostSavings / intellrAnnualCost)
            : 999;

        const paybackDays = intellrAnnualCost > 0
            ? Math.min(Math.round(intellrAnnualCost / (annualCostSavings / 365)), 365)
            : 0;

        return {
            hoursSaved: Math.round(hoursSaved),
            newTimeToHire,
            timeSavedDays,
            newCostPerHire,
            totalCostSavings,
            newNoShow,
            recruiterHoursFreed,
            recruitersEquivalent,
            roiMultiple: Math.min(roiMultiple, 999),
            paybackDays,
            monthlyVoiceNeeded,
            plan,
            intellrAnnualCost,
            annualCostSavings,
        };
    }, [positions, applicantsPerRole, timeToHire, costPerHire, noShowRate]);

    const formatCurrency = (n: number) => {
        if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
        if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
        if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
        return `₹${n}`;
    };

    const sliders = [
        { label: "Open Positions / Quarter", value: positions, setter: setPositions, min: 1, max: 200, suffix: "", icon: Users },
        { label: "Applicants per Role", value: applicantsPerRole, setter: setApplicantsPerRole, min: 20, max: 500, suffix: "", icon: Users },
        { label: "Avg. Time-to-Hire (days)", value: timeToHire, setter: setTimeToHire, min: 7, max: 90, suffix: "d", icon: Clock },
        { label: "Avg. Cost per Hire", value: costPerHire, setter: setCostPerHire, min: 10000, max: 500000, suffix: "", icon: IndianRupee, isCurrency: true },
        { label: "Candidate No-show Rate", value: noShowRate, setter: setNoShowRate, min: 5, max: 60, suffix: "%", icon: TrendingDown },
    ];

    return (
        <section className="py-32 bg-white relative overflow-hidden">
            <div className="absolute top-[30%] left-[10%] w-[600px] h-[600px] bg-[radial-gradient(circle_at_center,rgba(13,148,136,0.15),rgba(255,255,255,0)_70%)] rounded-full pointer-events-none" />

            <div className="container mx-auto px-4 relative z-10">
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 text-accent text-xs font-bold uppercase tracking-wider mb-5 border border-accent/10"
                    >
                        <Calculator className="w-3.5 h-3.5" />
                        ROI Calculator
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="font-display text-4xl md:text-6xl font-bold text-slate-900 mb-6"
                    >
                        Calculate Your <br /> <span className="text-accent italic">Hiring ROI</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="text-lg text-slate-500 max-w-xl mx-auto font-medium"
                    >
                        Plug in your numbers and see the impact of switching to AI-powered hiring.
                    </motion.p>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto"
                >
                    {/* Inputs */}
                    <div className="rounded-3xl bg-white border border-slate-100 p-8 shadow-sm">
                        <h3 className="font-bold text-lg text-slate-900 mb-8 flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-accent" />
                            Your Current Hiring
                        </h3>
                        <div className="space-y-8">
                            {sliders.map((s) => (
                                <div key={s.label}>
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="text-sm font-bold text-slate-600 flex items-center gap-2">
                                            <s.icon className="w-4 h-4 text-slate-400" />
                                            {s.label}
                                        </label>
                                        <span className="text-sm font-bold text-accent tabular-nums">
                                            {s.isCurrency ? formatCurrency(s.value) : `${s.value}${s.suffix}`}
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        min={s.min}
                                        max={s.max}
                                        step={s.isCurrency ? 5000 : 1}
                                        value={s.value}
                                        onChange={(e) => s.setter(Number(e.target.value))}
                                        className="w-full h-2 rounded-full bg-slate-100 appearance-none cursor-pointer accent-accent [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-accent [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-accent/30 [&::-webkit-slider-thumb]:cursor-grab"
                                    />
                                    <div className="flex justify-between mt-1.5">
                                        <span className="text-[10px] text-slate-400">{s.isCurrency ? formatCurrency(s.min) : `${s.min}`}</span>
                                        <span className="text-[10px] text-slate-400">{s.isCurrency ? formatCurrency(s.max) : `${s.max}`}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Recommended plan chip */}
                        <div className="mt-8 pt-6 border-t border-slate-100">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Recommended Plan</span>
                                <span className={`text-sm font-bold ${results.plan.color}`}>
                                    {results.plan.name}
                                    {results.plan.monthlyPrice > 0 && (
                                        <span className="font-normal text-slate-400 ml-1">
                                            · ₹{results.plan.monthlyPrice.toLocaleString()}/mo
                                        </span>
                                    )}
                                    {results.plan.monthlyPrice === 0 && (
                                        <span className="font-normal text-slate-400 ml-1">· Free</span>
                                    )}
                                </span>
                            </div>
                            <p className="text-[11px] text-slate-400 mt-1.5 flex items-start gap-1.5">
                                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                                Based on ~{results.monthlyVoiceNeeded} AI voice screenings/month
                                (10% of applicants after resume shortlist)
                            </p>
                        </div>
                    </div>

                    {/* Results */}
                    <div className="space-y-4">
                        {/* Key Results */}
                        <div className="rounded-3xl bg-slate-900 p-8 text-white">
                            <h3 className="font-bold text-lg mb-6 flex items-center gap-2">
                                <Zap className="w-5 h-5 text-accent" />
                                With Intellr.
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Clock className="w-4 h-4 text-accent" />
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">Screening Hours Saved</span>
                                    </div>
                                    <p className="text-3xl font-bold text-white">{results.hoursSaved}h</p>
                                    <p className="text-[10px] text-white/40 mt-1">
                                        ≈ {results.recruitersEquivalent} full-time recruiters
                                    </p>
                                </div>
                                <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <TrendingDown className="w-4 h-4 text-emerald-400" />
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">Cost Reduction</span>
                                    </div>
                                    <p className="text-3xl font-bold text-emerald-400">{formatCurrency(results.totalCostSavings)}</p>
                                    <p className="text-[10px] text-white/40 mt-1">
                                        {formatCurrency(costPerHire)} → {formatCurrency(results.newCostPerHire)}/hire
                                    </p>
                                </div>
                                <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Clock className="w-4 h-4 text-blue-400" />
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">Time-to-Hire</span>
                                    </div>
                                    <p className="text-3xl font-bold text-blue-400">{results.newTimeToHire}d</p>
                                    <p className="text-[10px] text-white/40 mt-1">
                                        From {timeToHire}d → {results.newTimeToHire}d ({Math.round((1 - results.newTimeToHire / timeToHire) * 100)}% faster)
                                    </p>
                                </div>
                                <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Users className="w-4 h-4 text-amber-400" />
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">No-show Rate</span>
                                    </div>
                                    <p className="text-3xl font-bold text-amber-400">{results.newNoShow}%</p>
                                    <p className="text-[10px] text-white/40 mt-1">
                                        From {noShowRate}% → {results.newNoShow}% ({noShowRate - results.newNoShow}pt drop)
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* ROI Summary */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="rounded-2xl bg-accent/5 border border-accent/10 p-5 text-center">
                                <TrendingUp className="w-6 h-6 text-accent mx-auto mb-2" />
                                <p className="text-3xl font-bold text-accent">
                                    {results.roiMultiple >= 999 ? "∞" : `${results.roiMultiple}x`}
                                </p>
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1">Annual ROI</p>
                                {results.intellrAnnualCost > 0 && (
                                    <p className="text-[10px] text-slate-400 mt-0.5">
                                        {formatCurrency(results.annualCostSavings)} saved vs {formatCurrency(results.intellrAnnualCost)} spent
                                    </p>
                                )}
                            </div>
                            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-5 text-center">
                                <Zap className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                                <p className="text-3xl font-bold text-emerald-600">
                                    {results.paybackDays === 0 ? "Day 1" : `${results.paybackDays}d`}
                                </p>
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1">Payback Period</p>
                            </div>
                        </div>

                        {/* Summary Line */}
                        <div className="rounded-2xl bg-slate-50 border border-slate-100 p-5">
                            <p className="text-sm text-slate-500 font-medium text-center">
                                With <span className="font-bold text-slate-900">{positions} positions</span> and{" "}
                                <span className="font-bold text-slate-900">{(positions * applicantsPerRole).toLocaleString()} total applicants</span>,
                                Intellr. saves your team{" "}
                                <span className="font-bold text-accent">{results.hoursSaved} hours</span> and{" "}
                                <span className="font-bold text-accent">{formatCurrency(results.totalCostSavings)}</span> per quarter.
                            </p>
                        </div>

                        {/* Methodology disclaimer */}
                        <p className="text-[10px] text-slate-400 text-center leading-relaxed px-2">
                            Estimates based on industry benchmarks: 35% cost-per-hire reduction, 45% faster time-to-hire,
                            40% fewer no-shows. Resume AI screening is unlimited on all plans.
                            Voice interview limits depend on your plan. Results vary by company and hiring process.
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default ROICalculatorSection;
